import { useState, useCallback, useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAuthStore } from '../stores/authStore';
import { useGameStore } from '../stores/gameStore';
import { signalRService } from '../services/signalRClient';

export function useSignalR() {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roomId, setRoomId] = useState<string | null>(null);
  const handlersRegistered = useRef(false);

  const { accessToken } = useAuthStore();
  const { setRoomState, setPlayer, removePlayer, reset } = useGameStore();

  // Sync roomId state with signalRService
  useEffect(() => {
    const currentRoomId = signalRService.roomId;
    if (currentRoomId !== roomId) {
      setRoomId(currentRoomId);
    }
  }, [roomId]);

  const setupEventHandlers = useCallback(
    (connection: signalR.HubConnection) => {
      if (handlersRegistered.current) return;
      handlersRegistered.current = true;

      // Primary state sync - replaces Colyseus onMessage('gameState')
      connection.on('GameState', (data: any) => {
        console.log('>>> Received GameState:', data);

        const extra = data.extra || {};

        setRoomState({
          roomId: data.roomId,
          gameType: data.gameType,
          phase: data.phase as 'waiting' | 'bidding' | 'playing' | 'scoring' | 'finished',
          currentTurnPlayerId: data.currentTurnPlayerId,
          turnTimer: data.turnTimer,
          mySessionId: connection.connectionId,
          // Dominoes specific
          boardTiles: [],
          leftEnd: -1,
          rightEnd: -1,
          boneyardCount: extra.boneyardCount ?? 0,
          hasSpinner: extra.hasSpinner ?? false,
          spinnerValue: extra.spinnerValue ?? -1,
          spinnerId: extra.spinnerId ?? '',
          leftArm: extra.leftArm || [],
          rightArm: extra.rightArm || [],
          topArm: extra.topArm || [],
          bottomArm: extra.bottomArm || [],
          openEnds: extra.openEnds || { left: -1, right: -1, top: -1, bottom: -1 },
          preSpinnerTiles: extra.preSpinnerTiles || [],
          validPlays: extra.validPlays || [],
          scoreToWin: data.scoreToWin ?? 150,
          // Spades specific
          team1Score: extra.team1Score ?? 0,
          team1Bags: extra.team1Bags ?? 0,
          team2Score: extra.team2Score ?? 0,
          team2Bags: extra.team2Bags ?? 0,
          currentTrick: extra.currentTrick || [],
          spadesBroken: extra.spadesBroken ?? false,
          bids: new Map(Object.entries(extra.bids || {})),
          tricksWon: new Map(Object.entries(extra.tricksWon || {})),
          roundNumber: extra.roundNumber ?? 1,
          leadSuit: extra.leadSuit ?? '',
          team1Bid: extra.team1Bid ?? 0,
          team2Bid: extra.team2Bid ?? 0,
          team1Tricks: extra.team1Tricks ?? 0,
          team2Tricks: extra.team2Tricks ?? 0,
          validCards: extra.validCards || [],
        });

        // Update all players
        for (const player of data.players) {
          setPlayer(player.sessionId, {
            sessionId: player.sessionId,
            userId: player.userId,
            displayName: player.displayName,
            avatarUrl: player.avatarUrl,
            seatIndex: player.seatIndex,
            team: player.team,
            isConnected: player.isConnected,
            isReady: player.isReady,
            score: player.score,
            hand: player.hand,
          });
        }
      });

      // Player lifecycle
      connection.on('PlayerJoined', (data: any) => {
        console.log('>>> PlayerJoined:', data);
        setPlayer(data.sessionId, {
          sessionId: data.sessionId,
          userId: data.userId,
          displayName: data.displayName,
          avatarUrl: data.avatarUrl,
          seatIndex: data.seatIndex,
          team: undefined,
          isConnected: true,
          isReady: false,
          score: 0,
          hand: [],
        });
      });

      connection.on('PlayerLeft', (data: any) => {
        console.log('>>> PlayerLeft:', data);
        removePlayer(data.sessionId);
      });

      connection.on('PlayerReconnected', (data: any) => {
        console.log('>>> PlayerReconnected:', data);
      });

      connection.on('PlayerDisconnected', (data: any) => {
        console.log('>>> PlayerDisconnected:', data);
      });

      // Game lifecycle
      connection.on('GameStarted', (data: any) => {
        console.log('>>> GameStarted:', data);
      });

      connection.on('GameEnded', (data: any) => {
        console.log('>>> GameEnded:', data);
        setRoomState({ phase: 'finished' });
      });

      connection.on('RoundEnded', (data: any) => {
        console.log('>>> RoundEnded:', data);
        const { setRoomState } = useGameStore.getState();
        setRoomState({
          roundResult: data.roundResult ?? null,
          roundScores: data.scores ?? null,
          showRoundEndOverlay: true,
        });
      });

      // Turn management
      connection.on('YourTurn', (data: any) => {
        console.log('>>> YourTurn:', data);
      });

      connection.on('TurnChanged', (data: any) => {
        console.log('>>> TurnChanged:', data);
        setRoomState({ currentTurnPlayerId: data.currentPlayerId });
      });

      // Game actions
      connection.on('CardPlayed', (data: any) => {
        console.log('>>> CardPlayed:', data);
        // Append to currentTrick for live display on the table
        const cardId = typeof data.card === 'string' ? data.card : data.card?.id;
        if (data.playerId && cardId) {
          const prev = useGameStore.getState().currentTrick;
          setRoomState({ currentTrick: [...prev, `${data.playerId}:${cardId}`] });
        }
      });

      connection.on('DominoPlaced', (data: any) => {
        console.log('>>> DominoPlaced:', data);
      });

      connection.on('TrickWon', (data: any) => {
        console.log('>>> TrickWon:', data);
      });

      connection.on('BidSubmitted', (data: any) => {
        console.log('>>> BidSubmitted:', data);
        if (data.playerId && data.bid !== undefined) {
          const prev = useGameStore.getState().bids;
          const updated = new Map(prev);
          updated.set(data.playerId, data.bid);
          setRoomState({ bids: updated });
        }
      });

      connection.on('BiddingComplete', (data: any) => {
        console.log('>>> BiddingComplete:', data);
      });

      connection.on('CardDrawn', (data: any) => {
        console.log('>>> CardDrawn:', data);
      });

      connection.on('CardDiscarded', (data: any) => {
        console.log('>>> CardDiscarded:', data);
      });

      connection.on('SpreadCreated', (data: any) => {
        console.log('>>> SpreadCreated:', data);
      });

      connection.on('LaidOff', (data: any) => {
        console.log('>>> LaidOff:', data);
      });

      connection.on('PlayerKnocked', (data: any) => {
        console.log('>>> PlayerKnocked:', data);
      });

      connection.on('PlayerDropped', (data: any) => {
        console.log('>>> PlayerDropped:', data);
      });

      connection.on('ScoreUpdated', (data: any) => {
        console.log('>>> ScoreUpdated:', data);
      });

      connection.on('ChatMessage', (data: any) => {
        console.log('>>> ChatMessage:', data);
      });

      connection.on('GameError', (data: any) => {
        console.error('>>> GameError:', data);
        setError(data.message || 'Game error');
      });

      connection.on('RoomCreated', (data: any) => {
        console.log('>>> RoomCreated:', data);
      });

      // Connection lifecycle
      connection.onclose((err) => {
        console.log('>>> Connection closed:', err);
        handlersRegistered.current = false;
        signalRService.setRoomId(null);
        setRoomId(null);
        reset();
      });

      connection.onreconnecting((err) => {
        console.log('>>> Reconnecting:', err);
      });

      connection.onreconnected((connectionId) => {
        console.log('>>> Reconnected with connectionId:', connectionId);
        // Re-request state after reconnection
        const currentRoomId = signalRService.roomId;
        if (currentRoomId) {
          connection.invoke('RequestState', currentRoomId).catch(console.error);
        }
      });
    },
    [setRoomState, setPlayer, removePlayer, reset]
  );

  const ensureConnection = useCallback(
    async (): Promise<signalR.HubConnection> => {
      if (!accessToken) {
        throw new Error('Not authenticated');
      }

      const connection = signalRService.createConnection(accessToken);
      setupEventHandlers(connection);

      if (connection.state === signalR.HubConnectionState.Disconnected) {
        await connection.start();
        console.log('SignalR connected, connectionId:', connection.connectionId);
      }

      return connection;
    },
    [accessToken, setupEventHandlers]
  );

  const joinRoom = useCallback(
    async (gameType: string, options: Record<string, unknown> = {}) => {
      // Check if already in a room
      if (signalRService.isConnected && signalRService.roomId) {
        console.log('Already in room:', signalRService.roomId);
        return;
      }

      setIsConnecting(true);
      setError(null);

      try {
        const connection = await ensureConnection();

        console.log('Creating room:', gameType, 'with options:', options);
        const newRoomId = await connection.invoke<string>('CreateRoom', gameType, {
          vsAI: options.vsAI ?? false,
          maxPlayers: options.maxPlayers,
          turnTimeLimit: options.turnTimeLimit,
          scoreLimit: options.scoreLimit,
          password: options.password,
        });

        if (newRoomId) {
          signalRService.setRoomId(newRoomId);
          setRoomId(newRoomId);
          console.log('Room created:', newRoomId);
        }

        setIsConnecting(false);
      } catch (err) {
        setIsConnecting(false);
        console.error('Failed to create room:', err);
        const message = err instanceof Error ? err.message : 'Failed to create room';
        setError(message);
        throw err;
      }
    },
    [ensureConnection]
  );

  const joinRoomById = useCallback(
    async (roomIdToJoin: string, _options: Record<string, unknown> = {}) => {
      if (signalRService.roomId === roomIdToJoin && signalRService.isConnected) {
        return;
      }

      setIsConnecting(true);
      setError(null);

      try {
        const connection = await ensureConnection();

        console.log('Joining room:', roomIdToJoin);
        await connection.invoke('JoinRoom', roomIdToJoin);

        signalRService.setRoomId(roomIdToJoin);
        setRoomId(roomIdToJoin);

        setIsConnecting(false);
      } catch (err) {
        setIsConnecting(false);
        const message = err instanceof Error ? err.message : 'Failed to join room';
        setError(message);
        throw err;
      }
    },
    [ensureConnection]
  );

  const leaveRoom = useCallback(async () => {
    const currentRoomId = signalRService.roomId;
    if (currentRoomId && signalRService.connection) {
      try {
        await signalRService.connection.invoke('LeaveRoom', currentRoomId);
      } catch {
        // Ignore errors during leave
      }
    }
    signalRService.setRoomId(null);
    setRoomId(null);
    reset();
  }, [reset]);

  const invoke = useCallback(
    async (method: string, ...args: unknown[]) => {
      const connection = signalRService.connection;
      const currentRoomId = signalRService.roomId;
      if (connection && currentRoomId) {
        try {
          await connection.invoke(method, currentRoomId, ...args);
        } catch (err) {
          console.error(`Failed to invoke ${method}:`, err);
        }
      }
    },
    []
  );

  return {
    connection: signalRService.connection,
    roomId,
    isConnecting,
    error,
    joinRoom,
    joinRoomById,
    leaveRoom,
    invoke,
  };
}
